import angular from 'angular';
import olLayerImage from 'ol/layer/Image';
import olSourceImageWMS from 'ol/source/ImageWMS';
import {getUid as olUtilGetUid} from 'ol/util';
import GmfDatasourceOGC from 'gmf/datasource/OGC';
import ngeoMiscDatepickerComponent from 'ngeo/misc/datepickerComponent';

import {QualityAggregateParam} from 'aprona/aprona.js';

/**
 * @type {angular.IModule}
 * @hidden
 */
const myModule = angular.module(
  'apronaQualityAggregateFormComponent',
  [ngeoMiscDatepickerComponent.name]
);

myModule.run(
  /**
   * @ngInject
   * @param {angular.ITemplateCacheService} $templateCache
   */
  ($templateCache) => {
    // @ts-ignore: webpack
    $templateCache.put('aprona/qualityaggregate/form', require('./formComponent.html'));
  }
);

myModule.value(
  'apronaQualityAggregateTemplateUrl',
  /**
   * @param {angular.IAttributes} $attrs Attributes.
   * @returns {string} The template url.
   */
  ($attrs) => {
    const templateUrl = $attrs.apronaQualityAggregateTemplateUrl;
    return templateUrl !== undefined ? templateUrl : 'aprona/qualityaggregate/form';
  },
);

/**
 * @param {angular.IAttributes} $attrs Attributes.
 * @param {function(angular.IAttributes): string} apronaQualityAggregateTemplateUrl Template function.
 * @returns {string} Template URL.
 * @private
 * @hidden
 */
apronaQualityAggregateTemplateUrl.$inject = ['$attrs', 'apronaQualityAggregateTemplateUrl'];
function apronaQualityAggregateTemplateUrl($attrs, apronaQualityAggregateTemplateUrl) {
  return apronaQualityAggregateTemplateUrl($attrs);
}


/**
 * @enum {string}
 * @hidden
 */
const Aggregate = {
  MIN: 'min',
  AVG: 'avg',
  MAX: 'max',
};

/**
 * @hidden
 */
export class Controller {
  /**
   * @param {JQuery} $element Element.
   * @param {angular.IScope} $scope Angular scope.
   * @param {angular.gettext.gettextCatalog} gettextCatalog The gettextCatalog service.
   * @param {import('gmf/theme/Themes').ThemesService} gmfThemes gmf Themes service.
   * @param {import('ngeo/statemanager/Service').StatemanagerService} ngeoStateManager The ngeo statemanager
   * @param {import('ngeo/datasource/DataSources').DataSource} ngeoDataSources Ngeo data sources service.
   * @ngdoc controller
   * @ngname ApronaQualityAggregateFormController
   */
  constructor($element, $scope, gettextCatalog, gmfThemes, ngeoStateManager, ngeoDataSources) {

    // Binding properties

    /**
     * @type {?import('ol/Map').default}
     */
    this.map = null;

    // Injected properties

    /**
     * @type {JQuery}
     * @private
     */
    this.element_ = $element;

    /**
     * @type {angular.IScope}
     * @private
     */
    this.$scope_ = $scope;

    /**
     * @type {import('gmf/theme/Themes').ThemesService}
     */
    this.gmfThemes_ = gmfThemes;

    /**
     * @type {import('ngeo/statemanager/Service').StatemanagerService}
     * @private
     */
    this.ngeoStateManager = ngeoStateManager;

    /**
     * @type {import('gmf/datasource/Datasource').Datasource}
     * @private
     */
    this.ngeoDataSources = ngeoDataSources;

    // Inner properties

    /**
     * @type {{ code: string, nom: string } | null}
     * @private
     */
    this.parametre = null;

    /**
     * @type {Date}
     * @private
     */
    this.start = new Date();
    this.start.setFullYear(this.start.getFullYear() - 1)

    /**
     * @type {Date}
     * @private
     */
    this.end = new Date();

    /**
     * @type {string}
     * @private
     */
    this.aggregate = Aggregate.AVG;

    /**
     * @type {JQuery | null}
     */
    this.$parametreTypeahead = null;

    /**
     * @type {string[]}
     */
    this.aggregates = [Aggregate.MIN, Aggregate.AVG, Aggregate.MAX]

    /**
     * @type {Object.<string, string>}
     */
    this.aggregateLabels = {
      [Aggregate.MIN]: gettextCatalog.getString('Minimum value'),
      [Aggregate.AVG]: gettextCatalog.getString('Average value'),
      [Aggregate.MAX]: gettextCatalog.getString('Maximum value'),
    };

    this.timeRangeMode = {
      widget: 'datepicker',
      maxValue: null,
      minValue: null,
      maxDefValue: this.end.toISOString(),
      minDefValue: this.start.toISOString(),
      mode: 'range',
      interval: [0, 1, 0, 0],
    };

    /**
     * @type {boolean}
     */
    this.keepLayer = false;

    /**
     * @type {boolean}
     */
    this.loading = false

    /**
     * @type {import('ol/source/ImageWMS').default}
     */
    this.olSource = null;

    /**
     * @type {import('ol/layer/Image').default}
     */
    this.olLayer = null;

    /**
     * @type {import('gmf/datasource/OGC').GmfDatasourceOGC}
     */
    this.datasource = null;

    /**
     * @type {string[]}
     */
    this.rules = [
      "< 80% Limite de potabilité",
      "> 80% Limite de potabilité",
      "> Limite de potabilité",
      "Limite de potabilité non renseignée",
    ]

    // Get layer name and some fields names catched as translatable string
    gettextCatalog.getString("agregat_qualite");
    gettextCatalog.getString("max_value");
    gettextCatalog.getString("avg_value");
    gettextCatalog.getString("min_value");
    gettextCatalog.getString("analyses");
    gettextCatalog.getString("limite_potabilite");

    this.restoreState();
  }

  /**
   * Called on initialization of the component.
   */
  $onInit() {
    this.$parametreTypeahead = $('input[name=parametre].typeahead').typeahead(
      {
        hint: true,
        highlight: true,
        minLength: 0
      },
      {
        name: 'source',
        source: new Bloodhound({
          datumTokenizer: (datum) => Bloodhound.tokenizers.whitespace(datum.code + " " + datum.nom),
          queryTokenizer: Bloodhound.tokenizers.whitespace,
          identify: (datum) => datum.code,
          remote: {
            url: '/aprona/parametres_autocomplete/?term=%QUERY%',
            wildcard: '%QUERY%',
          },
        }),
        display: (datum) => datum.nom + ' - ' + datum.code,
        limit: 2000,
        templates : {
          suggestion: function(data) {
            return '<div>' + data.nom + ' - ' + data.code + '</div>';
          },
        }
      }
    )
    if (this.parametre != null) {
      this.$parametreTypeahead.val(`${this.parametre.nom} - ${this.parametre.code}`)
    }

    this.$parametreTypeahead.bind('typeahead:select', (event, suggestion) => {
      this.onParametreChange(suggestion);
    })

    this.gmfThemes_.getOgcServersObject()
    .then((ogcServers) => {
      const ogcServer = ogcServers['source for image/png']
      this.createLayer(ogcServer);
      this.createDatasource(ogcServer);
      this.apply();
    });
  }

  createLayer(ogcServer) {
    this.olSource = new olSourceImageWMS({
      url: ogcServer.url,
      params: {
        FORMAT: 'image/png',
        LAYERS: 'agregat_qualite',
        SERVERTYPE: 'mapserver',
        ogcserver: 'source for image/png'
      }
    });
    this.olSource.on('imageloadstart', () => {
      this.$scope_.$apply(() => {
        this.loading = true;
      })
    })
    this.olSource.on('imageloadend', () => {
      this.$scope_.$apply(() => {
        this.loading = false;
      })
    })

    this.olLayer = new olLayerImage({
      visible: false,
      source: this.olSource
    })
    this.olLayer.setZIndex(999);
    this.map.addLayer(this.olLayer);
  }

  createDatasource(ogcServer) {
    // Create an external datasource for querying (selection)
    const id = Number(olUtilGetUid(this.olLayer));
    const options = {
      id: id,
      name: 'agregat_qualite',
      ogcImageType: 'image/png',
      ogcType: 'WMS',
      visible: true,
      wmsUrl: ogcServer.url,
      wmsInfoFormat: 'application/vnd.ogc.gml',
      wmsLayers: [
        {
          name: 'agregat_qualite',
          queryable: true,
        },
      ],
      wfsLayers:[
        {
          name: 'agregat_qualite',
          queryable: true,
        }
      ],
      wfsUrl: ogcServer.wfsUrl,
      wfsFeatureNS: 'http://mapserver.gis.umn.edu/mapserver',
      dimensions: {},
      dimensionsConfig: {
        parametre: null,
        start: null,
        end: null,
        aggregate: null,
      }
    };
    this.datasource = new GmfDatasourceOGC(options);
    this.ngeoDataSources.collection.push(this.datasource);
  }

  /**
   * Called when some component attribute has changed.
   */
  $onChanges(changes) {
    if (changes.active) {
      this.setLayerVisible(changes.active.currentValue || this.keepLayer);
      this.updateState();
    }
  }

  updateState() {
    if (this.active || this.keepLayer) {
      const state = {}
      state[QualityAggregateParam.PANEL_ACTIVE] = this.active;
      state[QualityAggregateParam.PARAMETRE] = JSON.stringify(this.parametre);
      state[QualityAggregateParam.START] = this.start.toISOString().split('T')[0];
      state[QualityAggregateParam.END] = this.end.toISOString().split('T')[0];
      state[QualityAggregateParam.AGGREGATE] = this.aggregate;
      this.ngeoStateManager.updateState(state);
    } else {
      this.ngeoStateManager.deleteParam(QualityAggregateParam.PANEL_ACTIVE);
      this.ngeoStateManager.deleteParam(QualityAggregateParam.PARAMETRE);
      this.ngeoStateManager.deleteParam(QualityAggregateParam.START);
      this.ngeoStateManager.deleteParam(QualityAggregateParam.END);
      this.ngeoStateManager.deleteParam(QualityAggregateParam.AGGREGATE);
    }
  }

  restoreState() {
    const parametreJson = this.ngeoStateManager.getInitialStringValue(QualityAggregateParam.PARAMETRE);
    if (parametreJson) {
      try {
        this.parametre = JSON.parse(parametreJson)
      } catch(e) {}
    }

    const startString = this.ngeoStateManager.getInitialStringValue(QualityAggregateParam.START)
    if (startString) {
      try {
        this.start = new Date(startString);
      } catch(e) {}
    }

    const endString = this.ngeoStateManager.getInitialStringValue(QualityAggregateParam.END)
    if (endString) {
      try {
        this.end = new Date(endString);
      } catch(e) {}
    }

    const aggregate = this.ngeoStateManager.getInitialStringValue(QualityAggregateParam.AGGREGATE)
    if (aggregate) {
      this.aggregate = aggregate;
    }
  }

  setLayerVisible(visible) {
    if (this.olLayer) {
      this.olLayer.setVisible(visible);
    }
  }

  onParametreChange(suggestion) {
    this.parametre = suggestion;
    this.apply();
  }

  onClearParametre() {
    this.parametre = null;
    this.$parametreTypeahead.val('');
    this.apply();
  }

  onStartChange() {
    this.apply();
  }

  onEndChange() {
    this.apply();
  }

  onAggregateChange() {
    this.apply();
  }

  apply() {
    if (!this.active) return

    this.updateState();

    // Update datasource using dimensions
    Object.assign(this.datasource.dimensions, {
      parametre: this.parametre ? this.parametre.code : undefined,
      start: this.start.toISOString().split('T')[0],
      end: this.end.toISOString().split('T')[0],
      aggregate: this.aggregate,
    })

    if (this.parametre === null) {
      this.setLayerVisible(false);
      return
    }

    this.olSource.updateParams({
      parametre: this.parametre.code,
      start: this.start.toISOString().split("T")[0],
      end: this.end.toISOString().split("T")[0],
      aggregate: this.aggregate,
    })

    this.setLayerVisible(true);
  }

  getLegendUrl(extraParams = {}) {
    if (this.olSource === null) {
      return null;
    }
    return this.olSource.getLegendUrl(
      undefined,
      {
        FORMAT: 'image/png',
        LAYER: 'agregat_qualite',
        SERVERTYPE: 'mapserver',
        TRANSPARENT: 'true',
        ogcserver: 'source for image/png',
        VERSION: '1.1.1',  // Avoid MapServer error: Missing required parameter SLD_VERSION
        ...extraParams
      }
    )
  }
}

Controller.$inject = [
  '$element',
  '$scope',
  'gettextCatalog',
  'gmfThemes',
  'ngeoStateManager',
  'ngeoDataSources',
];

myModule.component('apronaQualityAggregateForm', {
  bindings: {
    'active': '<',
    'map': '<',
  },
  controller: Controller,
  templateUrl: apronaQualityAggregateTemplateUrl,
});

export default myModule;
